import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, User, ArrowLeft, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Breadcrumb, useBreadcrumbs } from '@/components/ui/Breadcrumb';
import { BlogGrid } from '@/components/blog/BlogGrid';
import { useBlogPost, useBlogPosts } from '@/hooks/useBlog';
import { useLanguage } from '@/hooks/useLanguage';
import { formatBlogDate } from '@/utils/dateFormat';
import { Helmet } from 'react-helmet-async';
import { SEO } from '@/components/shared/SEO';

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const { post, loading, error } = useBlogPost(slug!);
  const { posts: relatedPosts } = useBlogPosts();
  const { t, currentLanguage } = useLanguage();

  if (loading) {
    return (
      <div className="min-h-screen">
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse max-w-4xl mx-auto">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6" />
            <div className="h-12 bg-gray-200 rounded w-3/4 mb-4" />
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-8" />
            <div className="h-96 bg-gray-200 rounded-xl mb-8" />
            <div className="space-y-4">
              <div className="h-4 bg-gray-200 rounded" />
              <div className="h-4 bg-gray-200 rounded" />
              <div className="h-4 bg-gray-200 rounded w-5/6" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Artículo no encontrado
          </h1>
          <p className="text-gray-600 mb-8">
            El artículo que buscas no existe o ha sido removido.
          </p>
          <Button asChild>
            <Link to="/blog">Ver Otros Artículos</Link>
          </Button>
        </div>
      </div>
    );
  }

  // Filter related posts (same category, excluding current post)
  const filteredRelatedPosts = relatedPosts
    .filter(p => p.id !== post.id && p.category === post.category)
    .slice(0, 3);

  // Generate URLs and dates
  const currentUrl = `${window.location.origin}/blog/${slug}`;
  const publishDate = new Date(post.publishedAt).toISOString();
  const modifiedDate = post.updatedAt ? new Date(post.updatedAt).toISOString() : publishDate;

  // Generate breadcrumb items
  const breadcrumbItems = useBreadcrumbs([
    {
      label: 'Blog',
      href: '/blog',
    },
    {
      label: post.category,
    },
  ]);

  // JSON-LD Structured Data
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        "@id": `${currentUrl}#article`,
        "url": currentUrl,
        "headline": post.title,
        "description": post.excerpt,
        "image": {
          "@type": "ImageObject",
          "url": post.featuredImage,
          "width": 1200,
          "height": 630
        },
        "datePublished": publishDate,
        "dateModified": modifiedDate,
        "author": {
          "@type": "Person",
          "name": post.author.name || "Tu Inmobiliaria",
          "description": post.author.bio || "",
          "image": post.author.image || ""
        },
        "publisher": {
          "@type": "Organization",
          "name": "Tu Inmobiliaria",
          "url": window.location.origin,
          "logo": {
            "@type": "ImageObject",
            "url": `${window.location.origin}/logo.png`
          }
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": currentUrl
        },
        "articleSection": post.category,
        "keywords": post.tags.join(", "),
        "wordCount": post.content.split(/\s+/).length,
        "timeRequired": `PT${post.readTime?.replace?.(/\D/g, '')}M`,
        "articleBody": post.content,
        "inLanguage": currentLanguage
      },
      {
        "@type": "WebPage",
        "@id": `${currentUrl}#webpage`,
        "url": currentUrl,
        "name": post.title,
        "description": post.excerpt,
        "primaryImageOfPage": {
          "@type": "ImageObject",
          "url": post.featuredImage
        },
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Inicio",
              "item": window.location.origin
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Blog",
              "item": `${window.location.origin}/blog`
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": post.category,
              "item": `${window.location.origin}/blog/categoria/${post.category.toLowerCase()}`
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": post.title,
              "item": currentUrl
            }
          ]
        }
      },
      {
        "@type": "WebSite",
        "@id": `${window.location.origin}#website`,
        "url": window.location.origin,
        "name": "Tu Inmobiliaria"
      }
    ]
  };

  return (
    <>
      <SEO
        title={post.title}
        description={post.excerpt}
        keywords={[...post.tags, 'inmobiliaria', 'bienes raíces', post.category.toLowerCase()]}
        canonicalPath={`/blog/${slug}`}
        ogImage={post.featuredImage}
        ogType="article"
      />

      <Helmet>
        {/* Article specific meta tags */}
        <meta name="article:published_time" content={publishDate} />
        <meta name="article:modified_time" content={modifiedDate} />
        <meta name="article:author" content={post.author.name || "Tu Inmobiliaria"} />
        <meta name="article:section" content={post.category} />
        {post.tags.map(tag => (
          <meta key={tag} name="article:tag" content={tag} />
        ))}

        {/* Additional OG Tags */}
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content={currentLanguage === 'es' ? 'es_ES' : 'en_US'} />
        <meta property="article:published_time" content={publishDate} />
        <meta property="article:modified_time" content={modifiedDate} />
        {post.tags.map(tag => (
          <meta key={`og-${tag}`} property="article:tag" content={tag} />
        ))}

        {/* Twitter Card Tags */}
        <meta name="twitter:creator" content="@tuinmobiliaria" />
        <meta name="twitter:site" content="@tuinmobiliaria" />

        {/* Additional SEO Tags */}
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="author" content={post.author.name || "Tu Inmobiliaria"} />
        <meta name="language" content={currentLanguage} />
        <meta name="news_keywords" content={post.tags.slice(0, 5).join(", ")} />

        {/* Reading time and content info */}
        <meta name="reading-time" content={post.readTime} />
        <meta name="word-count" content={post.content.split(/\s+/).length.toString()} />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>

      <div className="min-h-screen">
        {/* Header */}
        <div className="bg-gray-50 py-8">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb items={breadcrumbItems} className="mb-4" />
              <Button variant="ghost" asChild className="mb-6">
                <Link to="/blog">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Volver al Blog
                </Link>
              </Button>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <Badge variant="secondary">{post.category}</Badge>
                {post.featured && (
                  <Badge variant="destructive">DESTACADO</Badge>
                )}
              </div>

              <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                {post.title}
              </h1>

              <div className="flex flex-wrap items-center gap-6 text-gray-600 mb-6">
                <div className="flex items-center">
                  <Calendar className="h-5 w-5 mr-2" />
                  <span>{formatBlogDate(post.publishedAt)}</span>
                </div>
                <div className="flex items-center">
                  <Clock className="h-5 w-5 mr-2" />
                  <span>{post.readTime} {t('blog.readTime')}</span>
                </div>
                {post.author.name && (
                  <div className="flex items-center">
                    <User className="h-5 w-5 mr-2" />
                    <span>{post.author.name}</span>
                  </div>
                )}
              </div>

              <p className="text-xl text-gray-600 leading-relaxed">
                {post.excerpt}
              </p>
            </div>
          </div>
        </div>

        {/* Featured Image */}
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="relative h-96 md:h-[500px] rounded-xl overflow-hidden mb-8">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 pb-12">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Article Content */}
              <div className="lg:col-span-3">
                <div className="prose prose-lg max-w-none">
                  {post.content.split('\n').map((paragraph, index) => (
                    <p key={index} className="mb-6 text-gray-700 leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                </div>

                {/* Tags */}
                {post.tags.length > 0 && (
                  <div className="mt-8 pt-8 border-t">
                    <h3 className="text-lg font-semibold mb-4">{t('blog.tags')}</h3>
                    <div className="flex flex-wrap gap-2">
                      {post.tags.map((tag) => (
                        <Badge key={tag} variant="outline">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Author */}
                {post.author.name && (
                  <div className="bg-white p-6 rounded-xl shadow-lg border">
                    <h3 className="text-lg font-semibold mb-4">Autor</h3>
                    <div className="flex items-center space-x-4">
                      {post.author.image && (
                        <img
                          src={post.author.image}
                          alt={post.author.name}
                          className="w-16 h-16 rounded-full object-cover"
                        />
                      )}
                      <div>
                        <div className="font-semibold">{post.author.name}</div>
                        {post.author.bio && (
                          <div className="text-sm text-gray-600 mt-1">
                            {post.author.bio}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Share */}
                <div className="bg-white p-6 rounded-xl shadow-lg border">
                  <h3 className="text-lg font-semibold mb-4">{t('blog.share')}</h3>
                  <Button variant="outline" className="w-full">
                    <Share2 className="h-4 w-4 mr-2" />
                    Compartir Artículo
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Related Posts */}
        {filteredRelatedPosts.length > 0 && (
          <div className="bg-gray-50 py-16">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-3xl font-bold mb-8 text-center">
                  {t('blog.relatedPosts')}
                </h2>
                <BlogGrid posts={filteredRelatedPosts} />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}